import {
  AuthChallengeMessage,
  AuthChallengeResponseToken,
} from "@/app/types/user";
import { publicEnvConfig } from "../config/config";
import { HotelDetail, LiveRoomOfferData, SearchData } from "./types";
import { BookingResponse } from "@/app/types/booking";
import type { availabilityRoomContentResponse } from "@/app/types/availability";
import { getReferredBy } from "../services/service/localStorageService";
import type { Locale } from "next-intl";

export type ServiceResponse<T> = {
  error: Error | null;
  data: T | null;
};

export const getSearchData = async (
  locale: Locale,
  tokenId: string | null,
  walletId?: string | null,
  searchId?: string | null
): Promise<ServiceResponse<SearchData>> => {
  try {
    if (!(tokenId && walletId) && !searchId) {
      return {
        error: null,
        data: null,
      };
    }

    const url = searchId
      ? `/api/offers/${searchId}?lang=${locale}`
      : `/api/wallet/${walletId?.toLowerCase()}/hotelRequestToken/${tokenId}?lang=${locale}`;
    const response = await fetch(url, {
      method: "GET",
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data: SearchData = await response.json();

    return {
      error: null,
      data,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getAuthChallenge = async (
  walletId: string
): Promise<ServiceResponse<AuthChallengeMessage>> => {
  try {
    const response = await fetch(`/api/auth/${walletId}`);

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data = await response.json();

    return {
      error: null,
      data,
    };
  } catch (error) {
    console.log("getAuthChallenge Failed:", error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getUserData = async () => {
  try {
    const response = await fetch(`/api/user-data`, {
      cache: "no-store",
      credentials: "include",
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data ? data : null;
  } catch (error) {
    console.log("Verify User LoggedIn Failed:", error);
    return null;
  }
};

export const removeUserData = async () => {
  try {
    const response = await fetch(`/api/remove-user-data`, {
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data ? data : null;
  } catch (error) {
    console.log("Verify User LoggedIn Failed:", error);
    return null;
  }
};

export const verifySignedMessage = async (
  walletId: string,
  signature: string,
  payload: any
): Promise<ServiceResponse<AuthChallengeResponseToken>> => {
  try {
    const response = await fetch(`/api/auth`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        walletId,
        signature,
        referredBy: getReferredBy(),
        payload,
      }),
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(
        errorResponse.errorMessage || "An error occurred during verification"
      );
    }

    const data = await response.json();

    return {
      error: null,
      data,
    };
  } catch (error) {
    console.log("Verification Failed:", error);
    return {
      error:
        error instanceof Error
          ? error
          : new Error("An unknown error occurred during verification"),
      data: null,
    };
  }
};

export const getBookings = async (
  locale: Locale
): Promise<ServiceResponse<BookingResponse[]>> => {
  try {
    const response = await fetch(`/api/bookings?lang=${locale}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(
        errorResponse.errorMessage ||
          "An error occurred during booking data fetch"
      );
    }

    const data: { bookings: BookingResponse[] } = await response.json();

    return {
      error: null,
      data: data?.bookings,
    };
  } catch (error) {
    console.log("Booking data fetch Failed:", error);
    return {
      error:
        error instanceof Error
          ? error
          : new Error("An unknown error occurred during booking data fetch"),
      data: null,
    };
  }
};

export const getHotelData = async (
  locale: Locale,
  giataId: string
): Promise<ServiceResponse<HotelDetail>> => {
  try {
    const response = await fetch(
      `/api/hotel-detail/${giataId}?lang=${locale}`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data = await response.json();
    return {
      error: null,
      data: data?.hotelDetail,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getMinimalHotelData = async (
  locale: Locale,
  giataId: string
): Promise<ServiceResponse<HotelDetail>> => {
  try {
    const response = await fetch(
      `/api/hotel-detail/${giataId}?type=minimal&lang=${locale}`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data = await response.json();
    return {
      error: null,
      data: data?.hotelDetail,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getAvailabilityRoomContent = async (
  locale: Locale,
  id: string,
  code: string
): Promise<ServiceResponse<availabilityRoomContentResponse>> => {
  try {
    const response = await fetch(
      `/api/room-content/${id}/${code}?lang=${locale}`
    );

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }
    const data = await response.json();
    return {
      error: null,
      data: data?.hotelDetail,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const getLiveRoomOfferData = async (
  tokenId: string | null,
  walletId?: string | null,
  providerOfferId?: string,
  providerHotelId?: string
): Promise<ServiceResponse<LiveRoomOfferData>> => {
  try {
    const response = await fetch(
      `${publicEnvConfig.mainApiBaseUrl}/wallet/${walletId}/hotelRequestToken/${tokenId}/${providerOfferId}/${providerHotelId}`,
      {
        method: "GET",
      }
    );

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(errorResponse.errorMessage || "An error occurred");
    }

    const data: LiveRoomOfferData = await response.json();

    return {
      error: null,
      data,
    };
  } catch (error) {
    console.log(error);
    return {
      error:
        error instanceof Error ? error : new Error("An unknown error occurred"),
      data: null,
    };
  }
};

export const cancelBooking = async (
  locale: string,
  bookingId: string,
  giataId: string,
  wallet: string | undefined,
  params?: string | undefined
): Promise<any> => {
  try {
    const response = await fetch(
      `/api/booking/cancel?bookingId=${bookingId}&lang=${locale}&giataId=${giataId}&refundWalletAddress=${wallet}${params ? params : ""}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );
    const result = await response.json();
    return result;
  } catch (error: any) {
    return { error: error.message };
  }
};

export const getPointsAffiliateData = async (): Promise<
  ServiceResponse<any[]>
> => {
  try {
    const response = await fetch(`/api/user/points-referral`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      throw new Error(
        errorResponse.errorMessage ||
          "An error occurred during booking data fetch"
      );
    }

    return {
      error: null,
      data: await response.json(),
    };
  } catch (error) {
    console.log("Booking data fetch Failed:", error);
    return {
      error:
        error instanceof Error
          ? error
          : new Error("An unknown error occurred during booking data fetch"),
      data: null,
    };
  }
};
